import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { v4 } from "uuid";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCSwitch, RCCServicesApi, RCCButton,RCCTooltip } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import Tabs from "src/components/Tabs";
import Audit from "src/components/Audit";
import ModalSide from "src/components/Modal/styles";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { IPeriodType, IComponentTypePeriodsModals, IRefTypePeriodsModals, IPeriodForm } from "./interface";
import addAndChangeSchema from "./schemas";

const Modals: React.ForwardRefRenderFunction<IRefTypePeriodsModals, IComponentTypePeriodsModals> = ({
	paginationRef,
	onClose
}, ref) => {
	const { translate} = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const [selectedPeriodType, setSelectedPeriodType] = useState<IPeriodType>({} as IPeriodType);
	const controllerUrl = "PeriodTypes/";
	
	const actionButtonDelete = (row: IPeriodType) => {
		confirm(
			translate("Exclusão do tipo de período"),
			`${translate("Deseja realmente excluir tipo de período")}: ${row.iqb003Codigo}-${row.iqb003Descricao}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: `${controllerUrl}Delete`,
					urlParameters: {
						id: row.iqb003Id
					}
				});
	
				addToast({
					color: "green",
					description: translate("Tipo de período excluído com sucesso"),
					title: translate("Sucesso")
				});

				paginationRef?.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir tipo de período"), addToast);
			},
			true
		);
	};
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const observer = useRef<IntersectionObserver>();
	const [page,setPage] = useState(1);
	const [deletedPeriods,setDeletedPeriods] = useState<number[]>([]);
	const [totalPeriods,setTotalPeriods] = useState(0);
	const quantityPerPage = 5;

	const hasMorePeriods = () => totalPeriods > page * quantityPerPage;

	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange,
		control:controlAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange 
	} = useForm<IPeriodForm>();

	const { 
		fields: periodsFields, 
		prepend: prependPeriod, 
		remove: removePeriod,
		append: appendPeriod 
	} = useFieldArray({
		control: controlAddOrChange,
		name: "iqtbb004"
	});

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			await addAndChangeSchema(translate).validate(data, {
				abortEarly: false
			});
			let controllerData;

			if(isAdd){
				controllerData = await RCCServicesApi.postRequest({
					method: `${controllerUrl}Insert`,
					body: {
						...data
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: `${controllerUrl}Update`,
					body: {
						...data,
						iqb003Id:selectedPeriodType.iqb003Id,
						removedPeriods: deletedPeriods
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Tipo de período adicionado com sucesso") : translate("Tipo de período alterado com sucesso"),
				title: translate("Sucesso")
			});

			if(onClose && isAdd && controllerData?.data){
				onClose(controllerData.data as number);
			}

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef?.current?.reload();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar tipo de período") : translate("Erro ao alterar tipo de período"), addToast);
		}
	};

	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setSelectedPeriodType({iqb003Ativo:true} as IPeriodType); 
		setDeletedPeriods([]);
		setIsAdd(true); 
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (row: IPeriodType) => {
		try {
			resetAddOrChange(); 
			setDeletedPeriods([]);
			setIsAdd(false); 
			if(page === 1 && selectedPeriodType.iqb003Id === row.iqb003Id){
				requestPeriods();
			}else{
				setSelectedPeriodType(row); 
				setPage(1);
			}
			modalAddOrChange.current?.openModal(); 
		} catch (error) {
			standardErrorMessages(error, translate("Erro ao selecionar tipo de período") , addToast);
		}
		
	};

	const requestPeriods = async() => {

		const dataController = await RCCServicesApi.getRequest({
			method: `${controllerUrl}ListPeriods`,
			urlParameters: {
				periodTypeId:selectedPeriodType.iqb003Id,
				page,
				quantityPerPage
			}
		});
		appendPeriod(dataController.data.periods);

		if(totalPeriods === 0) 
			setTotalPeriods(dataController.data.total);
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	const addPeriod = () => {
		const newPeriod = {
			id:v4(),
			iqb004Id: 0,
			iqb004Codigo:"",
			iqb004Descricao: "",
			iqb004Desccompleta:""
		};

		prependPeriod(newPeriod);	
	};

	const deletePeriod = (index:number) => {
		const removed = {...periodsFields[index]}; 
		if  (removed.iqb004Id){
			setDeletedPeriods((prevDeletedState) => [...prevDeletedState,removed.iqb004Id]);
		}
		removePeriod(index);
	};

	const lastRowElementRef = useCallback((row) => {
		if (observer.current){
			observer.current.disconnect();
		}
		observer.current = new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting && hasMorePeriods()) {
				setPage((prevState) => prevState+1);
			}
		});
		if (row) observer.current.observe(row);
	}, [totalPeriods]);

	useEffect(() => {
		if (isAdd || !selectedPeriodType.iqb003Id) return;
		requestPeriods();
	}, [page,selectedPeriodType.iqb003Id]);

	return (
		<>
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar tipo de período") : translate("Alterar tipo de período"),
					icon: {
						name: "building"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				withoutForm
				styleInline={ModalSide}
			>
				<Tabs
					name="periodTypeTab"
					tabInit={0}
					options={[{
						label: translate("Dados"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
									justifyContent="flex-end"
								>
									<RCCGrid item>
										<Audit data={selectedPeriodType} type="pop" position="left"/>
									</RCCGrid>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCInput 
										register={registerAddOrChange}
										name="iqb003Codigo"
										label={translate("Código")}
										maxLength={20}
										defaultValue={selectedPeriodType.iqb003Codigo}
										error={errorsAddOrChange.iqb003Codigo}
										readOnly={!isAdd}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCInput 
										register={registerAddOrChange}
										name="iqb003Descricao"
										label={translate("Descrição")}
										maxLength={60}
										defaultValue={selectedPeriodType.iqb003Descricao}
										error={errorsAddOrChange.iqb003Descricao}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCSwitch name="iqb003Ativo" label={translate("Ativo")} register={registerAddOrChange} 
										defaultChecked ={selectedPeriodType.iqb003Ativo}
									/>
								</RCCGrid>
							</RCCGrid>
						)
					},
					{
						label: translate("Períodos"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
									justifyContent="space-between"
								>
									<RCCGrid item styleInline="align-self:flex-end;">
										<RCCTooltip
											text={translate("Adicionar período")}
											color="purple">
											<RCCButton
												icon={{name: "plus"}}
												color="purple"
												onClick={addPeriod}
												layout="isRounded"
												size="sm"
											/>
										</RCCTooltip>
									</RCCGrid>
									<hr />
								</RCCGrid>
								<RCCGrid 
									item 
									container 
									styleInline="overflow-y:auto; max-height:60vh;"
									justifyContent="space-between"
									alignItems="center"
								> 
									<RCCGrid 
										item 
										container 
										direction="column"
										alignItems="center"
									> 
										{periodsFields.map((period,index) => (
											<RCCGrid 
												container
												item
												xs={12}
												sm={10}
												key = {period.id}
											>
												<div ref = {periodsFields.length === index+1 && !isAdd ? lastRowElementRef:null} style={{visibility:"hidden"}}></div>
												<RCCGrid
													container
													item
													justifyContent="space-between"
												>
													<RCCGrid item xs={10}>
														<input 
															type="number"
															value={period.iqb004Id}
															{...registerAddOrChange(`iqtbb004.${index}.iqb004Id` as const)} 
															style={{ display: "none" }} 
															readOnly
														/>
														<RCCInput 
															register={registerAddOrChange}
															name={`iqtbb004.${index}.iqb004Codigo`}
															label={translate("Código")}
															maxLength={20}
															defaultValue={period.iqb004Codigo}
															error={errorsAddOrChange.iqtbb004 && errorsAddOrChange.iqtbb004[index]?.iqb004Codigo}
															readOnly={!!(period.iqb004Id && !isAdd)}
															required
															autoFocus={!!(!period.iqb004Codigo && index === 0)}
														/>
													</RCCGrid>
													<RCCGrid item styleInline="align-self: flex-end;">
														<RCCTooltip
															text={translate("Excluir período")}
															color="red"
															position="left">
															<RCCButton
																icon={{name: "trash"}}
																color="red"
																onClick={() => {deletePeriod(index);}}
																layout="isRounded"
																size="sm"
															/>	
														</RCCTooltip>
													</RCCGrid>		
												</RCCGrid>
												<RCCGrid
													container
													item
													justifyContent="space-between"
													wrap = "nowrap"
													spacing = {1}
												>
													<RCCGrid item container>
														<RCCInput 
															register={registerAddOrChange}
															name={`iqtbb004.${index}.iqb004Descricao`}
															label={translate("Descrição")}
															maxLength={60}
															defaultValue={period.iqb004Descricao}
															error={errorsAddOrChange.iqtbb004 && errorsAddOrChange.iqtbb004[index]?.iqb004Descricao}
															required
														/>
													</RCCGrid>
													<RCCGrid item container>
														<RCCInput 
															register={registerAddOrChange}
															name={`iqtbb004.${index}.iqb004Desccompleta`}
															label={translate("Descrição completa")}
															maxLength={200}
															defaultValue={period.iqb004Desccompleta}
															error={errorsAddOrChange.iqtbb004 && errorsAddOrChange.iqtbb004[index]?.iqb004Desccompleta}
														/>		
													</RCCGrid>		
												</RCCGrid>
												<RCCGrid
													container
													item
													justifyContent="space-between"
												>
													<DateTimePickerRange
														control={controlAddOrChange}
														nameInit = {`iqtbb004.${index}.iqb004Dataini`}
														nameEnd = {`iqtbb004.${index}.iqb004Datafim`}
														label = {translate("Datas do período")}
														type = "datePicker"
														defaultValueInit = {period.iqb004Dataini}
														required
														defaultValueEnd = {period.iqb004Datafim}
														error={errorsAddOrChange.iqtbb004 && (errorsAddOrChange.iqtbb004[index]?.iqb004Dataini||errorsAddOrChange.iqtbb004[index]?.iqb004Datafim)}	
													/>
												</RCCGrid>
												<hr/>
											</RCCGrid>
										))}
									</RCCGrid>
								</RCCGrid>
							</RCCGrid>
						)
					}
					]}
				/>
			</Modal>
		</>
	);
};

export default forwardRef(Modals);

